/**
 * "Make this song discoverable?" confirmation.
 *
 * THE ONE PLACE A SONG BECOMES DISCOVERABLE, from the user's side. Visibility is the
 * owner's decision about their own music, so it is asked for rather than inferred —
 * pressing the action opens this, and only confirming it here reaches
 * `songsStore.setVisibility`.
 *
 * NOT "PUBLISH", ANYWHERE IN THIS FLOW. "Publish" already means the Publisher — real
 * distribution to Spotify and Apple Music, behind a payment and a release submission —
 * and this is a Lyrixa-only listing decision that costs nothing and reverses in one
 * press. Two different things sharing a verb is how someone ends up believing they have
 * released a track.
 *
 * The body names the three sections the song can appear in, because "discoverable" on
 * its own does not say where. They are the Home page's three views of the one public
 * catalogue (lib/discovery), so this sentence is a description of what actually happens
 * rather than a promise about placement.
 *
 * IT SHOWS HOW THE SONG WILL BE CREDITED, BEFORE THE DECISION IS MADE. "Created by Amara
 * with Lyrixa AI" — the exact line the catalogue tile will carry (Batch 7 §17). A listing
 * decision is partly a decision about being named, and the one moment that matters is
 * before confirming rather than after discovering it on a public page. Composed by
 * lib/attribution and passed in as a string, so this dialog holds no name, no store
 * knowledge and no second copy of the template.
 *
 * Omitted entirely when there is no name to show, rather than shown as a partial line —
 * see lib/attribution for why nothing is invented.
 */
import { Globe } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import { c } from '../../styles/theme';
import type { Song } from '../../types/domain';

export interface MakeDiscoverableModalProps {
  /** The song pending the decision, or null when closed. */
  song: Song | null;
  /**
   * How the catalogue will credit this song — `lib/attribution`'s line, already composed.
   *
   * A STRING RATHER THAN THE PIECES, so the dialog cannot assemble a second version of the
   * sentence the cards use, and cannot be handed a name to hardcode. Null when there is no
   * creator to name, which renders no preview at all.
   */
  attribution?: string | null;
  onClose: () => void;
  onConfirm: () => void;
}

export default function MakeDiscoverableModal({
  song,
  attribution,
  onClose,
  onConfirm,
}: MakeDiscoverableModalProps) {
  return (
    <ConfirmDialog
      open={Boolean(song)}
      onClose={onClose}
      title="Make this song discoverable?"
      icon={<Globe size={19} color={c.accent} />}
      confirmLabel="Make Discoverable"
      confirmIcon={<Globe size={13} />}
      onConfirm={onConfirm}
    >
      <p
        style={{
          fontSize: 12,
          color: c.text2,
          // The margin moves to the attribution row when there is one, so the spacing
          // above the buttons is the same either way.
          margin: attribution ? '0 0 10px' : '0 0 18px',
          lineHeight: 1.5,
        }}
      >
        "{song?.title}" will appear in the Lyrixsa Catalogue and may be found by other listeners in
        Featured, Trending and Explore. You can change this at any time.
      </p>
      {/*
        ---- HOW IT WILL BE CREDITED ----
        The same line the catalogue tile carries, shown before the decision rather than
        after it. Boxed and labelled so it reads as a preview of a public page and not as
        another sentence of the explanation.

        Rendered only when a name exists. There is no placeholder: a dialog claiming "you
        will be credited" without saying as whom would be a promise this build cannot keep
        for a song with no creator on the record.
      */}
      {attribution && (
        <div
          style={{
            border: `1px solid ${c.border}`,
            background: c.surface2,
            borderRadius: 8,
            padding: '8px 10px',
            margin: '0 0 18px',
          }}
        >
          <p
            style={{
              fontSize: 9,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: c.text2,
              margin: 0,
            }}
          >
            Shown on the catalogue
          </p>
          {/* text1 and a step up in size: this is the quoted line itself, so it should read
              as the thing rather than as a note about it. */}
          <p style={{ fontSize: 11.5, color: c.text1, margin: '3px 0 0', lineHeight: 1.4 }}>
            {attribution}
          </p>
        </div>
      )}
    </ConfirmDialog>
  );
}
